import type { CSSProperties } from 'react'
import type { VideoStatus } from '../types'

// Bounds for the preview media frame so a pathological image (e.g. a 1×1000
// banner) can't blow out the card. 0.5 = tall 1:2 portrait, 1.91 = the widest
// common feed image (LinkedIn/Facebook link card).
const MEDIA_RATIO_MIN = 0.5
const MEDIA_RATIO_MAX = 1.91

/**
 * Inline `aspect-ratio` style for an image preview frame, derived from the real
 * image ratio (width/height) so the frame matches the image instead of a guessed
 * default. Returns `undefined` when no usable ratio is known, so callers fall
 * back to their static aspect class. Clamped to a sane range.
 */
export function imageAspectStyle(ratio?: number | null): CSSProperties | undefined {
  if (typeof ratio !== 'number' || !Number.isFinite(ratio) || ratio <= 0) return undefined
  const clamped = Math.min(MEDIA_RATIO_MAX, Math.max(MEDIA_RATIO_MIN, ratio))
  return { aspectRatio: String(clamped) }
}

/** Convert a business name into a social handle (e.g. "ACME & Co." → "acmeco") */
export function toSocialHandle(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '')
}

/** Shared copy for terminal reel/video generation failures. */
export const FAILED_REEL_MESSAGE = "Couldn't generate this reel. Your credit's back." as const

/**
 * Compact variant for the small DTO-backed tiles (calendar week cards, cockpit
 * queue tiles) — the refund reassurance clause wraps to a wall of text at
 * ~150px, and Matan's call (2026-08-30) is that the concession alone carries
 * it there. The bigger Home/Posts cards keep the full message below.
 */
export function shortFailedMediaMessage(noun: string): string {
  return `Couldn't generate this ${noun}.`
}

export function failedMediaMessage(noun: string): string {
  return `${shortFailedMediaMessage(noun)} Your credit's back.`
}

/** Reel/video file still generating — poster may exist but no playable URL yet. */
export function isPendingVideo(content: {
  videoUrl?: string | null
  videoStatus?: VideoStatus
}): boolean {
  return content.videoStatus === 'generating' && !content.videoUrl
}

/** Video generation failed before a playable URL landed. */
export function isFailedVideo(content: {
  videoUrl?: string | null
  videoStatus?: VideoStatus
}): boolean {
  return content.videoStatus === 'failed' && !content.videoUrl
}

/**
 * Card-level generating snake for feed-style previews. When a poster exists
 * and video is generating, `GeneratingVideoPoster` owns the media overlay —
 * skip the card-level duplicate (same rule as TikTok/YouTube previews).
 */
export function showPreviewCardGeneratingOverlay(
  isGenerating: boolean,
  isGeneratingVideo: boolean,
  posterUrl?: string | null
): boolean {
  return (isGenerating && !(isGeneratingVideo && posterUrl)) || (isGeneratingVideo && !posterUrl)
}

/** Feed-preview video mode + card overlay state (IG/FB/LI/Pinterest). */
export function getFeedVideoPreviewState(
  content: { videoUrl?: string | null; videoStatus?: VideoStatus },
  isGenerating: boolean,
  posterUrl?: string | null
) {
  const isGeneratingVideo = isPendingVideo(content)
  const isFailedReel = isFailedVideo(content)
  const isVideoMode = !!content.videoUrl || isGeneratingVideo || isFailedReel
  const showCardOverlay = showPreviewCardGeneratingOverlay(
    isGenerating,
    isGeneratingVideo,
    posterUrl
  )
  return { isGeneratingVideo, isFailedReel, isVideoMode, showCardOverlay }
}

/**
 * Vertical-only previews (TikTok/YT): broader than `isPendingVideo` because
 * `generatingFields` may list `videoUrl` before `videoStatus` flips.
 */
export function isGeneratingVideoFromFields(
  content: { videoUrl?: string | null; videoStatus?: VideoStatus },
  generatingFields: Set<string>
): boolean {
  if (content.videoUrl) return false
  return (
    generatingFields.has('videoUrl') ||
    generatingFields.has('video') ||
    content.videoStatus === 'generating'
  )
}

/**
 * Tailwind arbitrary-selector overrides that re-skin the embedded social
 * preview chrome (built for white surfaces) into the T3 dark theme:
 *   bg-white        → --bg-2  (card)
 *   bg-gray-50/100  → --bg-3  (muted surface)
 *   text-gray-900/700/black → --paper        (primary text)
 *   text-gray-600/500       → --paper-dim    (meta/secondary)
 *   text-gray-400/300       → --paper-faint  (placeholders, timestamps)
 *   border-gray-*           → --line
 *
 * The values must use Tailwind's arbitrary-value syntax (`!bg-[var(--bg-2)]`)
 * because the Taka tokens (`bg-bg-2`, `text-paper`, etc.) are hand-rolled
 * CSS classes in `design-system.css`, not Tailwind utilities — Tailwind
 * can't compile them as utility values inside `[&_…]:!utility` selectors.
 *
 * Brand-tinted accents (IG/LI/FB blues, hashtag links) aren't gray and so
 * pass through unchanged, keeping each platform recognisable.
 *
 * Section-hover affordances (group/section overlays for Ask AI / Edit) are
 * left interactive — surfaces using this in a read-only context (PostModal)
 * compose `'[&_.group\\/section]:!pointer-events-none'` on top.
 */
export const DARK_PREVIEW_OVERRIDES = [
  // Backgrounds.
  '[&_.bg-white]:!bg-[var(--bg-2)]',
  '[&_.bg-gray-50]:!bg-[var(--bg-3)]',
  '[&_.bg-gray-100]:!bg-[var(--bg-3)]',
  '[&_.bg-gray-200]:!bg-[var(--bg-4)]',
  // Text.
  '[&_.text-black]:!text-[var(--paper)]',
  '[&_.text-gray-900]:!text-[var(--paper)]',
  '[&_.text-gray-700]:!text-[var(--paper)]',
  '[&_.text-gray-600]:!text-[var(--paper-dim)]',
  '[&_.text-gray-500]:!text-[var(--paper-dim)]',
  '[&_.text-gray-400]:!text-[var(--paper-faint)]',
  '[&_.text-gray-300]:!text-[var(--paper-faint)]',
  // Borders. The IG card sets its border via inline style (`border: 1px
  // solid #e5e7eb`) so a `.border-gray-200` selector doesn't match it —
  // we re-target the same element via `.bg-white` and override the
  // colour through `!border` (className-with-!important beats the inline
  // shorthand for the longhand `border-color`). The `.border-t/.border-b`
  // dividers use Tailwind's default border colour, so we override them too.
  '[&_.bg-white]:!border-[var(--line)]',
  '[&_.border-t]:!border-[var(--line)]',
  '[&_.border-b]:!border-[var(--line)]',
  '[&_.border-gray-100]:!border-[var(--line)]',
  '[&_.border-gray-200]:!border-[var(--line)]',
  '[&_.border-gray-300]:!border-[var(--line)]',
].join(' ')

export function isSocialPostEmpty(content: {
  imageUrl?: string | null
  imageAttachmentId?: string | null
  videoUrl?: string | null
  videoAttachmentId?: string | null
  caption?: string
  message?: string
  hashtags?: string[]
  mediaItems?: unknown[]
}): boolean {
  // mediaItems counts as content — without this check, a carousel sibling that
  // has mediaItems mirrored from the primary but hasn't generated its own
  // message/caption yet would render the "Your post will appear here"
  // placeholder instead of the actual slides. hasAsset() in
  // PostModalPreviewPane already considers mediaItems as an asset, so the two
  // checks would otherwise disagree and the skeleton wouldn't render either.
  return (
    !content.imageUrl &&
    !content.imageAttachmentId &&
    !content.videoUrl &&
    !content.videoAttachmentId &&
    !content.caption &&
    !content.message &&
    (content.hashtags?.length ?? 0) === 0 &&
    (content.mediaItems?.length ?? 0) === 0
  )
}
